import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import useTranslation from '../../hooks/useTranslation';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState(false);

  useEffect(() => {
    // Detect whether the URL actually carries an invite/recovery token.
    // Supabase implicit flow: hash fragment (#access_token=...&type=invite / type=recovery)
    // Supabase PKCE flow: query param (?code=...)
    const hash = window.location?.hash || '';
    const search = window.location?.search || '';
    const hasAuthToken =
      hash?.includes('access_token') ||
      hash?.includes('type=invite') ||
      hash?.includes('type=recovery') ||
      search?.includes('code=');

    // If there is no token at all in the URL this is a direct/invalid visit → fail immediately.
    if (!hasAuthToken) {
      setSessionError(true);
      return;
    }

    // Subscribe FIRST so we never miss an event that fires during getSession().
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        // Always clear any prior error and show the form — even if a late event
        // arrives after the safety timeout has already set sessionError=true.
        setSessionReady(true);
        setSessionError(false);
      }
    });

    // Proactively check for an already-established session (e.g. user refreshed
    // the page after the token was already exchanged).
    supabase?.auth?.getSession()?.then(({ data: { session }, error: sessionErr }) => {
      if (session) {
        setSessionReady(true);
        setSessionError(false);
        return;
      }
      if (sessionErr) {
        // Supabase returned an explicit error → the token is genuinely bad.
        setSessionError(true);
        return;
      }
      // No session yet but a token IS present in the URL — give Supabase a
      // generous 10 s window to fire SIGNED_IN / PASSWORD_RECOVERY before
      // concluding the link is invalid.
      const safetyTimer = setTimeout(() => {
        supabase?.auth?.getSession()?.then(({ data: { session: s } }) => {
          if (!s) {
            setSessionError(true);
          }
        });
      }, 10000);

      // Store timer id on the subscription object so we can cancel it if the
      // auth event arrives before the timeout fires.
      subscription._safetyTimer = safetyTimer;
    });

    return () => {
      if (subscription?._safetyTimer) {
        clearTimeout(subscription?._safetyTimer);
      }
      subscription?.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (password?.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({ password });
      if (updateError) {
        setError(updateError?.message || 'Failed to update password. Please try again.');
      } else {
        setSuccess(true);
        // Sign out so user logs in fresh with new password
        await supabase?.auth?.signOut();
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <Helmet>
        <title>Reset Password - OnSitePM</title>
      </Helmet>
      {/* LEFT PANEL — Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col items-center justify-center"
        style={{ background: 'linear-gradient(145deg, #1a2535 0%, #2C3E50 50%, #1e3a5f 100%)' }}
      >
        {/* Blueprint grid overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,107,44,0.6) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,107,44,0.6) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
        />

        {/* Decorative corners */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-16 h-16 border-t-2 border-l-2 opacity-20" style={{ borderColor: '#FF6B2C' }} />
          <div className="absolute bottom-10 right-10 w-16 h-16 border-b-2 border-r-2 opacity-20" style={{ borderColor: '#FF6B2C' }} />
          <div className="absolute top-0 right-0 w-px h-full opacity-10" style={{ background: 'linear-gradient(to bottom, transparent, #FF6B2C, transparent)' }} />
        </div>

        {/* Centered branding */}
        <div className="relative z-10 text-center px-12 max-w-md">
          <div className="flex items-center justify-center mb-8">
            <div className="relative">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)', boxShadow: '0 8px 32px rgba(255,107,44,0.4)' }}
              >
                <Icon name="HardHat" size={36} color="#FFFFFF" />
              </div>
              <div
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-md flex items-center justify-center"
                style={{ background: '#2C3E50', border: '2px solid #FF6B2C' }}
              >
                <Icon name="Building2" size={12} color="#FF6B2C" />
              </div>
            </div>
          </div>

          <h1 className="text-5xl font-bold text-white mb-3 tracking-tight">
            OnSite<span style={{ color: '#FF6B2C' }}>PM</span>
            <sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup>
          </h1>
          <p className="text-lg font-medium mb-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Construction Project Management
          </p>
          <div
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full"
            style={{ background: 'rgba(255,107,44,0.12)', border: '1px solid rgba(255,107,44,0.25)' }}
          >
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF6B2C' }} />
            <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {t('login.poweredBy')}
            </span>
          </div>
        </div>
      </div>
      {/* RIGHT PANEL */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)' }}
            >
              <Icon name="HardHat" size={22} color="#FFFFFF" />
            </div>
            <div className="ml-3">
              <span className="text-2xl font-bold text-foreground">
                OnSite<span style={{ color: '#FF6B2C' }}>PM</span>
                <sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup>
              </span>
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border p-8 shadow-subtle">

            {/* Card header */}
            <div className="mb-8">
              <div className="hidden lg:flex items-center gap-3 mb-6">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)' }}
                >
                  <Icon name="HardHat" size={16} color="#FFFFFF" />
                </div>
                <span className="text-lg font-bold text-foreground">
                  OnSite<span style={{ color: '#FF6B2C' }}>PM</span>
                  <sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup>
                </span>
              </div>

              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(255,107,44,0.1)', border: '1px solid rgba(255,107,44,0.2)' }}
                >
                  <Icon name="LockKeyhole" size={20} color="#FF6B2C" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Set New Password</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Choose a strong password for your OnSitePM account.
              </p>
            </div>

            {/* Success state */}
            {success ? (
              <div className="space-y-5">
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-green-50 border border-green-200">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
                    <Icon name="ShieldCheck" size={24} color="#16a34a" />
                  </div>
                  <h3 className="text-base font-semibold text-green-800 mb-1">Password updated!</h3>
                  <p className="text-sm text-green-700">
                    Your password has been changed successfully. You can now sign in with your new password.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  <Icon name="LogIn" size={16} />
                  <span>Go to Sign In</span>
                </Button>
              </div>
            ) : sessionError ? (
              /* Invalid / expired link state */
              (<div className="space-y-5">
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-red-50 border border-red-200">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
                    <Icon name="LinkOff" size={24} color="#dc2626" />
                  </div>
                  <h3 className="text-base font-semibold text-red-800 mb-1">Link expired or invalid</h3>
                  <p className="text-sm text-red-700">
                    This password reset link is no longer valid. Please request a new one.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  <Icon name="RefreshCw" size={16} />
                  <span>Request New Link</span>
                </Button>
                <p className="text-xs text-center mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  Remember your password?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="font-semibold"
                    style={{ color: '#FF6B2C' }}
                  >
                    Sign In
                  </button>
                </p>
              </div>)
            ) : !sessionReady ? (
              /* Loading / verifying token */
              (<div className="flex flex-col items-center justify-center py-8 space-y-3">
                <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#FF6B2C', borderTopColor: 'transparent' }} />
                <p className="text-sm text-muted-foreground">Verifying your reset link…</p>
              </div>)
            ) : (
              /* Reset form */
              (<form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Input
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e?.target?.value); setError(''); }}
                    placeholder="Min. 8 characters"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-150"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
                <div className="relative">
                  <Input
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e?.target?.value); setError(''); }}
                    placeholder="Re-enter your new password"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-150"
                  >
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
                {/* Password strength hint */}
                {password?.length > 0 && (
                  <div className="flex items-center gap-2">
                    {[...Array(4)]?.map((_, i) => {
                      const strength = Math.min(Math.floor(password?.length / 3), 4);
                      const colors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500'];
                      return (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-all duration-200 ${i < strength ? colors?.[strength - 1] : 'bg-border'}`}
                        />
                      );
                    })}
                    <span className="text-xs text-muted-foreground ml-1">
                      {password?.length < 3 ? 'Weak' : password?.length < 6 ? 'Fair' : password?.length < 9 ? 'Good' : 'Strong'}
                    </span>
                  </div>
                )}
                {error && (
                  <div className="flex items-center space-x-2 p-3 rounded-lg bg-red-50 border border-red-200">
                    <Icon name="AlertCircle" size={16} className="text-red-600" />
                    <span className="text-sm text-red-600">{error}</span>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting || !password || !confirmPassword}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="Loader2" size={18} className="animate-spin" />
                      <span>Updating…</span>
                    </>
                  ) : (
                    <>
                      <Icon name="ShieldCheck" size={18} />
                      <span>Update Password</span>
                    </>
                  )}
                </Button>
                <p className="text-xs text-center mt-3" style={{ color: 'var(--muted-foreground)' }}>
                  Remember your password?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="font-semibold"
                    style={{ color: '#FF6B2C' }}
                  >
                    Sign In
                  </button>
                </p>
              </form>)
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs font-semibold text-foreground">
              OnSitePM<sup style={{ fontSize: '0.55em', verticalAlign: 'super', lineHeight: 0 }}>™</sup>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('login.poweredBy')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
